import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Questionnaire } from '@local/client-contracts';
import { questionnaireContent } from '../../questionnaire.content';
import { isEqual } from 'lodash';

export type QuestionnaireType = 'question' | 'response';

@UntilDestroy()
@Component({
  selector: 'mapping-worksheet',
  templateUrl: './mapping-worksheet.component.html',
  styleUrls: ['./mapping-worksheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MappingWorksheetComponent {
  readonly MAX_QUESTIONS = 8;
  readonly questionnaireContent = questionnaireContent;
  private _worksheet: Questionnaire.Worksheet;
  addQuestionnaireClicked: boolean;

  @Input() set worksheet(worksheet: Questionnaire.Worksheet) {
    this._worksheet = worksheet;
  }

  @Output() changed = new EventEmitter<Questionnaire.Worksheet>();

  get worksheet(): Questionnaire.Worksheet {
    return this._worksheet;
  }

  get disabledAddButton(): boolean {
    const lastQuestionnaire = this.worksheet.questionnaire[this.worksheet.questionnaire.length - 1];
    if (this.worksheet.questionnaire.length >= this.questionnaireContent.maxQuestions) {
      return true;
    }
    if (this.isErrorInput(lastQuestionnaire, 'question') || this.isErrorInput(lastQuestionnaire, 'response')) {
      return true;
    }
    return !lastQuestionnaire.questionColumn?.address && !lastQuestionnaire.responseColumn?.address;
  }

  constructor(private cdr: ChangeDetectorRef) {}

  isErrorInput(questionnaire: Questionnaire.Questionnaire, type: QuestionnaireType): boolean {
    const primaryColumn = type === 'response' ? questionnaire?.responseColumn?.address : questionnaire?.questionColumn?.address;
    if (primaryColumn && !this.questionnaireContent.currentColumnRegex.test(primaryColumn)) {
      return true;
    }
    const secondColumn = type === 'response' ? questionnaire?.questionColumn?.address : questionnaire?.responseColumn?.address;
    if (primaryColumn && isEqual(primaryColumn, secondColumn) && type === 'response') {
      return true;
    }
    return !primaryColumn && !!secondColumn;
  }

  updateColumn(newColumn: string, type: QuestionnaireType, index: number) {
    const questionnaire = this.worksheet.questionnaire[index];
    const header = this.worksheet.headerCells.get(newColumn.toUpperCase())?.toString();
    const column = { address: newColumn, header };

    if (!this.questionnaireContent.currentColumnRegex.test(newColumn)) {
      column.header = questionnaireContent.emptyColumn;
    } else if (!header) {
      column.header = questionnaireContent.undefinedColumn;
    }

    switch (type) {
      case 'question':
        questionnaire.questionColumn = column;
        break;
      case 'response':
        questionnaire.responseColumn = column;
        break;
    }

    this.changed.emit(this.worksheet);
    this.cdr.markForCheck();
  }

  deleteQuestionnaire(index: number) {
    if (this.worksheet && this.worksheet.questionnaire.length > index) {
      this.worksheet.questionnaire.splice(index, 1);
    }
    this.changed.emit(this.worksheet);
    this.cdr.markForCheck();
  }

  addQuestionnaire() {
    if (this.worksheet.questionnaire?.length >= 8) {
      return;
    }
    this.addQuestionnaireClicked = true;

    if (this.worksheet) {
      this.worksheet.questionnaire.push({ questionColumn: { address: '', header: '' }, responseColumn: { address: '', header: '' } });
    }
    this.changed.emit(this.worksheet);
    this.cdr.markForCheck();
  }
}
